/* eslint-disable indent */
import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Select,
    MenuItem,
    Table,
    TableRow,
    TableCell,
    TableHead,
    Stack,
    Chip,
    TableBody,
    TableContainer,
    Paper,
    Pagination,
    SelectChangeEvent,
    Radio,
    TextField,
    Typography,
    Skeleton,
    PaperProps,
    Divider,
} from '@mui/material';
import Box from '@mui/material/Box';
import Draggable from 'react-draggable';
import { toast } from 'react-toastify';
import SpringDataPage, { BasicPage, defaultPage } from '../../../../@types/spring-data-page';
import { Component, ComponentSummary } from '../../../../@types/component';
import { FaultTree } from '../../../../@types/faultTree';
import { FaultTreeNode } from '../../../../@types/faultTreeNode';
import componentService from '../../../../services/component.service';
import { CanvasEnvironment } from '../../../../utils/canvas/canvas-environment';
import { CanvasActionType } from '../../../../@types/canvas-action-type';
import { useWorkspaceContext } from '../../../../context/workspaceContext';

interface SelectComponentProps {
    open: boolean;
    onClose: () => void;
    env: CanvasEnvironment | null;
}

const pageSizes: number[] = [10, 25, 50];

function PaperComponent(props: PaperProps): React.ReactElement {
    const nodeRef = React.useRef(null);
    return (
        <Draggable nodeRef={nodeRef} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

/**
 * Picks a Component (and a node within its fault tree) to copy a subtree from. The picked node id
 * is stashed in localStorage.copyContents and FAULTTREENODE_PASTE_COMPONENT is dispatched -- the
 * canvas editor then pastes that subtree beneath the selected canvas node.
 */
const SelectComponentModal: React.FC<SelectComponentProps> = ({ open, onClose }) => {
    const { setCurrentCanvasAction } = useWorkspaceContext();

    const [page, setPage] = useState<BasicPage>({ ...defaultPage, size: pageSizes[0] });
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [componentsPage, setComponentsPage] = useState<SpringDataPage<ComponentSummary> | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const [selectedComponent, setSelectedComponent] = useState<ComponentSummary | null>(null);
    const [selectedTree, setSelectedTree] = useState<FaultTree | null>(null);
    const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
    const [loadingTree, setLoadingTree] = useState<boolean>(false);

    const resetSelection = (): void => {
        setSelectedComponent(null);
        setSelectedTree(null);
        setSelectedNodeId(null);
    };

    const fetchComponents = (nextPage: BasicPage, term: string): void => {
        setLoading(true);
        resetSelection();
        componentService
            .fetchPagedComponents(nextPage, { searchTerm: term || undefined })
            .then(setComponentsPage)
            .catch((error: Error) => toast.error(error.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (open) {
            const initialPage: BasicPage = { ...defaultPage, size: pageSizes[0] };
            setPage(initialPage);
            setSearchTerm('');
            fetchComponents(initialPage, '');
        } else {
            setComponentsPage(null);
            resetSelection();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const onSearchKeyDown = (event: React.KeyboardEvent): void => {
        if (event.key === 'Enter') {
            const nextPage = { ...page, offset: 0 };
            setPage(nextPage);
            fetchComponents(nextPage, searchTerm);
        }
    };

    const onPageChange = (_event: React.ChangeEvent<unknown>, pageNumber: number): void => {
        const nextPage = { ...page, offset: pageNumber - 1 };
        setPage(nextPage);
        fetchComponents(nextPage, searchTerm);
    };

    const onPageSizeChange = (event: SelectChangeEvent<number>): void => {
        const nextPage: BasicPage = { ...page, size: event.target.value as number, offset: 0 };
        setPage(nextPage);
        fetchComponents(nextPage, searchTerm);
    };

    const onSelectComponent = (component: ComponentSummary): void => {
        setSelectedComponent(component);
        setSelectedTree(null);
        setSelectedNodeId(null);
        setLoadingTree(true);
        componentService
            .getComponent(component.id)
            .then((full: Component) => {
                const tree: FaultTree | undefined = full.faultTree;
                setSelectedTree(tree ?? null);
                setSelectedNodeId(tree ? (tree.rootNodeId ?? tree.faultTreeNodes[0]?.id ?? null) : null);
            })
            .catch((error: Error) => toast.error(error.message))
            .finally(() => setLoadingTree(false));
    };

    const handleConfirm = (): void => {
        if (selectedNodeId == null) {
            return;
        }
        localStorage.copyContents = JSON.stringify({ type: 'fault_tree_node', id: selectedNodeId });
        setCurrentCanvasAction({ actionType: CanvasActionType.FAULTTREENODE_PASTE_COMPONENT });
        onClose();
    };

    const sortedNodes: FaultTreeNode[] = selectedTree
        ? [...selectedTree.faultTreeNodes].sort((a, b) => a.uniqueId.localeCompare(b.uniqueId))
        : [];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            maxWidth="md"
            fullWidth
        >
            <DialogTitle id="draggable-dialog-title" sx={{ cursor: 'move' }}>Select Component</DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search components by name, description or id, then press Enter"
                    value={searchTerm}
                    onChange={(e): void => setSearchTerm(e.target.value)}
                    onKeyDown={onSearchKeyDown}
                    sx={{ mt: 1, mb: 1 }}
                />

                {loading && (
                    <Stack spacing={1} sx={{ mt: 1 }}>
                        {[...Array(4)].map((_, i) => (
                            <Skeleton key={i} variant="rectangular" height={36} />
                        ))}
                    </Stack>
                )}

                {!loading && (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300, overflowY: 'auto' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" />
                                    <TableCell>Name</TableCell>
                                    <TableCell>Version</TableCell>
                                    <TableCell>State</TableCell>
                                    <TableCell>Group</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody sx={{ cursor: 'pointer' }}>
                                {componentsPage?.content.map((component) => (
                                    <TableRow
                                        key={component.id}
                                        hover
                                        selected={selectedComponent?.id === component.id}
                                        onClick={(): void => onSelectComponent(component)}
                                    >
                                        <TableCell padding="checkbox">
                                            <Radio size="small" checked={selectedComponent?.id === component.id} />
                                        </TableCell>
                                        <TableCell>{component.name}</TableCell>
                                        <TableCell>{component.version ? `v${component.version}` : '—'}</TableCell>
                                        <TableCell>
                                            <Chip size="small" variant="outlined" label={component.state} />
                                        </TableCell>
                                        <TableCell>{component.groupId ? `#${component.groupId}` : 'Global'}</TableCell>
                                    </TableRow>
                                ))}
                                {componentsPage?.content.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5}>
                                            <Typography variant="body2" color="textSecondary">
                                                No components found.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {componentsPage?.page && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Select variant="standard" value={page.size} onChange={onPageSizeChange} sx={{ fontSize: '14px' }}>
                            {pageSizes.map((size) => (
                                <MenuItem key={size} value={size}>{size} per page</MenuItem>
                            ))}
                        </Select>
                        <Pagination
                            count={componentsPage.page.totalPages}
                            size="small"
                            showFirstButton
                            showLastButton
                            page={page.offset + 1}
                            siblingCount={0}
                            boundaryCount={1}
                            onChange={onPageChange}
                        />
                    </Stack>
                )}

                {selectedComponent && (
                    <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 1 }} />
                        <Typography variant="subtitle2" gutterBottom>
                            Subtree to copy from &ldquo;{selectedComponent.name}&rdquo;
                            {selectedComponent.version ? ` v${selectedComponent.version}` : ''}
                        </Typography>
                        {loadingTree && <Skeleton variant="rectangular" height={120} />}
                        {!loadingTree && selectedTree && (
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200, overflowY: 'auto' }}>
                                <Table size="small">
                                    <TableBody>
                                        {sortedNodes.map((node) => (
                                            <TableRow
                                                key={node.id}
                                                hover
                                                selected={selectedNodeId === node.id}
                                                sx={{ cursor: 'pointer' }}
                                                onClick={(): void => setSelectedNodeId(node.id)}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Radio size="small" checked={selectedNodeId === node.id} />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{node.uniqueId}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="textSecondary">
                                                        {node.name} ({node.type})
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">Cancel</Button>
                <Button onClick={handleConfirm} color="primary" disabled={selectedNodeId == null}>
                    Copy Subtree
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SelectComponentModal;
